import type { Maybe } from '@passionware/monads';
import { useCallback, useRef } from 'react';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { Dialog, DialogContentHeadless } from 'v5/design-system/Dialog';
import { CreateCalloutDialogContent } from 'v5/features/callouts/CreateCalloutDialogContent';
import { MountElementProps } from 'v5/messages/mount-element';
import { CalloutCreateFormData } from 'v5/features/callouts/callout-create.types';
import { WithServices } from 'v5/platform/ts/services';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

export interface CreateCalloutDialogProps
  extends WithServices<
    [
      WithResourceTypeService,
      WithCalloutsService,
      WithAppScopeService,
      WithEnterpriseService
    ]
  > {
  onSubmit?: (data: CalloutCreateFormData) => Promise<void> | void;
  enterpriseWorkspaceId?: Maybe<number>;
}

export function CreateCalloutDialog({
  open,
  onOpenChange,
  onSubmit,
  services,
  enterpriseWorkspaceId
}: CreateCalloutDialogProps & MountElementProps) {
  const escapeKeyDownRef = useRef<
    ((event: KeyboardEvent) => void) | undefined
  >();

  const registerEscapeKeyDownHandler = useCallback(
    (handler: ((event: KeyboardEvent) => void) | undefined) => {
      escapeKeyDownRef.current = handler;
    },
    []
  );

  return (
    <UnsavedChangesProtection
      open={open}
      onOpenChange={onOpenChange}
      beforeUnloadMessage="You have unsaved changes in the callout form!"
    >
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless
            onEscapeKeyDown={event => escapeKeyDownRef.current?.(event)}
          >
            <CreateCalloutDialogContent
              open={dialogProps.open}
              onSubmit={onSubmit}
              services={services}
              enterpriseWorkspaceId={enterpriseWorkspaceId}
              registerEscapeKeyDownHandler={registerEscapeKeyDownHandler}
            />
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}
